import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Breadcrumb,
    Button,
    Grid,
    Icon,
    Input,
    Menu,
    Message,
    Segment
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import styled from 'styled-components';
import { BIParceiroStatusActions } from '../../actions/BIParceiroActions';

const EMPTY_VALUE = '—';

const Page = styled.div`
    padding: 1em 0;
`;

const TopMenu = styled(Menu)`
    &&& {
        background: transparent;
        border: none;
        border-bottom: solid 1.5px #1884af;
        box-shadow: none;
        border-radius: 0 0 5px 5px;
    }

    &&& > .item {
        padding: 0;
    }

    &&& .ui.breadcrumb .section {
        padding: 1.5em 1.5em;
    }

    &&& .ui.breadcrumb .section:first-child {
        padding-right: 0;
    }

    &&& .ui.breadcrumb .active.section {
        background-color: #1884af;
        color: #fff;
    }
`;

const Toolbar = styled.div`
    display: grid;
    grid-template-columns: minmax(280px, 440px) minmax(280px, 1fr);
    gap: 1.5em 3em;
    align-items: end;
    padding: 1.25em .75em 1.5em;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
        gap: 1em;
    }
`;

const SearchField = styled.label`
    display: flex;
    flex-direction: column;
    gap: .45em;
    color: #1884af;
    font-weight: 700;
    min-width: 0;
`;

const SearchControls = styled.div`
    display: grid;
    grid-template-columns: minmax(150px, 1fr) auto;
    gap: .75em;

    @media (max-width: 430px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const TabPanel = styled.div`
    padding: 1em .75em;
`;

const StatusHeading = styled.div`
    display: flex;
    align-items: flex-start;
    gap: .8em;
    font-size: 1.1em;
    line-height: 1.5;
`;

const ActionBox = styled.div`
    margin-top: 1em;
    padding-top: 1em;
    border-top: 1px solid #e3e7e9;

    strong {
        margin-right: .35em;
    }
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .9em 2em;
    margin: 1.5em 0;

    @media (max-width: 650px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const Detail = styled.div`
    overflow-wrap: anywhere;

    strong {
        margin-right: .35em;
    }
`;

const Contacts = styled.ol`
    margin: .5em 0 1.5em;
    padding-left: 1.6em;

    li {
        padding: .25em 0;
        overflow-wrap: anywhere;
    }
`;

const RuleBox = styled.div`
    padding: 1em;
    border-left: 3px solid #b7bdc1;
    border-radius: 4px;
    background: #f7f7f7;
    color: #777;
    font-style: italic;
    overflow-wrap: anywhere;
`;

export const isValidCodparc = value => /^\d+$/.test(String(value).trim())
    && Number(String(value).trim()) > 0;

export const getStatusPresentation = (status) => ({
    0: { icon: 'question circle outline', color: 'black' },
    1: { icon: 'check circle outline', color: 'green' },
    2: { icon: 'warning sign', color: 'yellow' },
    3: { icon: 'ban', color: 'red' }
}[Number(status)] || { icon: 'question circle outline', color: 'black' });

const displayValue = value => (
    value === null || value === undefined || String(value).trim() === ''
        ? EMPTY_VALUE
        : value
);

export const formatCRMDate = (value) => {
    if (!value) {
        return EMPTY_VALUE;
    }

    const textValue = String(value).trim();
    if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(textValue)) {
        return textValue;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return textValue;
    }

    const pad = number => String(number).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const summaryFields = [
    ['Supervisor', 'supervisor', displayValue],
    ['Vendedor', 'vendedor', displayValue],
    ['Na carteira desde', 'carteira_desde', formatCRMDate],
    ['Último orçamento', 'ultimo_orcamento', formatCRMDate],
    ['Última venda', 'ultima_venda', formatCRMDate],
    ['Regra fiscal', 'regra_fiscal', displayValue]
];

export function BIParceiroCRM({
    menu = [],
    statusData,
    statusLoading,
    statusError,
    history,
    BIParceiroStatusActions: consultarStatus
}) {
    const [codparc, setCodparc] = useState('');
    const [validationError, setValidationError] = useState('');
    const submittingRef = useRef(false);
    const possuiPermissao = menu.some(item => Number(item.menuid) === 41 && Number(item.status) === 1);

    useEffect(() => {
        if (!possuiPermissao) {
            history.push('/Home');
        }
    }, [history, possuiPermissao]);

    const contacts = useMemo(() => (
        Array.from({ length: 6 }, (_, index) => (
            statusData ? statusData[`contato_programado_${index + 1}`] : null
        ))
    ), [statusData]);

    if (!possuiPermissao) {
        return null;
    }

    const submit = () => {
        if (statusLoading || submittingRef.current) {
            return;
        }

        const normalizedCodparc = String(codparc).trim();
        if (!isValidCodparc(normalizedCodparc)) {
            setValidationError('Informe um código de parceiro válido.');
            return;
        }

        setValidationError('');
        submittingRef.current = true;
        Promise.resolve(consultarStatus(Number(normalizedCodparc)))
            .catch(() => {})
            .finally(() => {
                submittingRef.current = false;
            });
    };

    const statusPresentation = getStatusPresentation(statusData && statusData.status);
    const feedbackMessage = validationError || (statusError && statusError.message);

    return (
        <Page>
            <TopMenu stackable size="mini">
                <Menu.Item>
                    <Breadcrumb size="mini">
                        <Breadcrumb.Section href="/Home">Home</Breadcrumb.Section>
                        <Breadcrumb.Divider icon="right chevron" />
                        <Breadcrumb.Section active>CRM</Breadcrumb.Section>
                    </Breadcrumb>
                </Menu.Item>
            </TopMenu>

            <Toolbar data-testid="crm-toolbar">
                <SearchField>
                    Código do parceiro
                    <SearchControls>
                        <Input
                            aria-label="Código do parceiro"
                            icon="search"
                            placeholder="Código"
                            value={codparc}
                            onChange={event => setCodparc(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    submit();
                                }
                            }}
                            disabled={statusLoading}
                        />
                        <Button
                            color="blue"
                            type="button"
                            icon
                            labelPosition="left"
                            loading={statusLoading}
                            disabled={statusLoading}
                            onClick={submit}
                        >
                            <Icon name="search" />
                            Buscar
                        </Button>
                    </SearchControls>
                </SearchField>

                <SearchField>
                    Nome do parceiro
                    <Input
                        aria-label="Nome do parceiro"
                        value={!statusLoading && !feedbackMessage && statusData
                            ? displayValue(statusData.nomeparc)
                            : ''}
                        readOnly
                    />
                </SearchField>
            </Toolbar>

            <Grid columns={1} doubling>
                <Grid.Column>
                    <Menu secondary pointing>
                        <Menu.Item active>
                            <Icon name="check circle outline" />
                            Status
                        </Menu.Item>
                    </Menu>

                    <TabPanel>
                        {feedbackMessage && (
                            <Message
                                negative={!validationError}
                                warning={!!validationError}
                                content={feedbackMessage}
                            />
                        )}

                        {statusLoading && (
                            <Message icon>
                                <Icon name="circle notched" loading />
                                <Message.Content>Consultando parceiro...</Message.Content>
                            </Message>
                        )}

                        {!statusLoading && !feedbackMessage && !statusData && (
                            <Message info content="Informe um código de parceiro para consultar." />
                        )}

                        {!statusLoading && !feedbackMessage && statusData && (
                            <div data-testid="status-result">
                                <Segment>
                                    <StatusHeading data-testid="status-indicator">
                                        <Icon
                                            name={statusPresentation.icon}
                                            color={statusPresentation.color}
                                            size="large"
                                        />
                                        <div>
                                            <strong>Status: </strong>
                                            {displayValue(statusData.analise)}
                                        </div>
                                    </StatusHeading>
                                    <ActionBox>
                                        <strong>Ação:</strong>
                                        {displayValue(statusData.acao)}
                                    </ActionBox>
                                </Segment>

                                <DetailGrid>
                                    {summaryFields.map(([label, field, formatter]) => (
                                        <Detail key={field}>
                                            <strong>{label}:</strong>
                                            {formatter(statusData[field])}
                                        </Detail>
                                    ))}
                                </DetailGrid>

                                <strong>Contato programado:</strong>
                                <Contacts>
                                    {contacts.map((contact, index) => (
                                        <li key={index} data-testid="scheduled-contact">
                                            {displayValue(contact)}
                                        </li>
                                    ))}
                                </Contacts>

                                <strong>Regra:</strong>
                                <RuleBox data-testid="rule-box">
                                    {displayValue(statusData.regra)}
                                </RuleBox>
                            </div>
                        )}
                    </TabPanel>
                </Grid.Column>
            </Grid>
        </Page>
    );
}

const mapStateToProps = state => ({
    menu: state.LoginReducer.menu,
    statusData: state.BIParceiroReducer.statusData,
    statusLoading: state.BIParceiroReducer.statusLoading,
    statusError: state.BIParceiroReducer.statusError
});

export default withRouter(connect(mapStateToProps, {
    BIParceiroStatusActions
})(BIParceiroCRM));
